import React, { useState, useEffect } from "react";
import { Select, Input, Button } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import "../styles/globalTable.css";

const PaginationTable = ({ total, paginationConfig, width }) => {
  const { current, pageSize, onChange, pageSizeOptions } = paginationConfig;
  const [manualPageInput, setManualPageInput] = useState(current.toString());

  useEffect(() => {
    setManualPageInput(current.toString());
  }, [current]);

  const handleManualInputSubmit = () => {
    const inputPage = parseInt(manualPageInput, 10);
    const totalPages = Math.ceil(total / pageSize);
    if (!isNaN(inputPage) && inputPage >= 1 && inputPage <= totalPages) {
      onChange(inputPage, pageSize);
      setManualPageInput(inputPage.toString());
    }
  };

  const handlePageSizeChange = (current, size) => {
    onChange(1, size);
  };

  const totalPages = Math.ceil(total / pageSize);
  const pageNumbers = Array.from(Array(totalPages).keys(), (x) => x + 1);

  // Calculate displayed pages based on the provided logic
  let startIndex, endIndex;
  if (totalPages <= 5) {
    startIndex = 0;
    endIndex = totalPages - 1;
  } else if (current <= 3) {
    startIndex = 0;
    endIndex = 4;
  } else if (current >= totalPages - 2) {
    startIndex = totalPages - 5;
    endIndex = totalPages - 1;
  } else {
    startIndex = current - 3;
    endIndex = current + 1;
  }

  const displayedPages = pageNumbers.slice(startIndex, endIndex + 1);

  const handleNumberPaginate = (clickedPage) => {
    onChange(clickedPage, pageSize);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-3 sm:gap-0">
        <Button
          disabled={current === 1}
          onClick={() => onChange(current - 1, pageSize)}
          className="pagination-nav-btn pagination-nav-btn-prev w-full sm:w-auto"
        >
          <ArrowLeftOutlined className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Previous</span>
        </Button>
        <div className="flex items-center gap-1 sm:gap-3 flex-wrap justify-center max-w-full overflow-x-auto">
          {totalPages > 5 && current > 3 && (
            <>
              <div
                className={`w-8 h-8 flex items-center justify-center rounded-full cursor-pointer text-xs sm:text-sm ${
                  1 === current
                    ? "bg-gray-200 text-customColor font-medium"
                    : "hover:bg-gray-200 text-black font-medium"
                }`}
                onClick={() => handleNumberPaginate(1)}
              >
                1
              </div>
              {current > 4 && (
                <span className="px-1 sm:px-2 text-black cursor-default text-xs sm:text-sm">
                  ...
                </span>
              )}
            </>
          )}
          {displayedPages.map((page) => (
            <div
              key={page}
              className={`w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full cursor-pointer text-xs sm:text-sm ${
                page === current
                  ? "bg-gray-200 text-customColor font-medium"
                  : "hover:bg-gray-200 text-black font-medium"
              }`}
              onClick={() => handleNumberPaginate(page)}
            >
              {page}
            </div>
          ))}
          {totalPages > 5 && current < totalPages - 2 && (
            <>
              {current < totalPages - 3 && (
                <span className="px-1 sm:px-2 text-black cursor-default text-xs sm:text-sm">
                  ...
                </span>
              )}
              <div
                className={`w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full cursor-pointer text-xs sm:text-sm ${
                  totalPages === current
                    ? "bg-gray-200 text-customColor font-medium"
                    : "hover:bg-gray-200 text-black font-medium"
                }`}
                onClick={() => handleNumberPaginate(totalPages)}
              >
                {totalPages}
              </div>
            </>
          )}
          {totalPages > 5 && current < totalPages - 2 && (
            <div className="flex items-center ml-2 sm:ml-4 hidden md:flex">
              <label
                htmlFor="manualPageInput"
                className="mr-2 text-black text-xs sm:text-sm font-medium"
              >
                Go to
              </label>
              <Input
                id="manualPageInput"
                type="number"
                value={manualPageInput}
                onChange={(e) => setManualPageInput(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && handleManualInputSubmit()
                }
                className="p-1 border border-gray-300 rounded-[4px] text-xs sm:text-sm"
                max={totalPages}
                min={1}
                style={{ width: 60 }}
              />
              <Button
                onClick={handleManualInputSubmit}
                className="pagination-go-to-btn border-2 rounded-[4px] relative ml-2 items-center p-2 sm:p-3.5 min-w-[30px] h-[30px] justify-center text-xs sm:text-sm font-medium gap-2 bg-white text-black hover:bg-gray-100 focus:outline-none focus:ring-2 focus:text-primary hover:text-primary"
              >
                <ArrowRightOutlined className="w-3 h-3" />
              </Button>
            </div>
          )}
        </div>
        <Button
          disabled={current === Math.ceil(total / pageSize)}
          onClick={() => onChange(current + 1, pageSize)}
          className="pagination-nav-btn pagination-nav-btn-next w-full sm:w-auto"
        >
          <span className="hidden sm:inline">Next</span>
          <ArrowRightOutlined className="w-3.5 h-3.5" />
        </Button>
      </div>
      <div className="flex flex-col sm:flex-row justify-center items-center font-medium !text-[12px] sm:!text-[13px] text-black mt-4 gap-3 sm:gap-5">
        <span className="font-[Poppins] font-medium !text-[12px] sm:!text-[13px] text-black text-center sm:text-left">
          Showing {(current - 1) * pageSize + 1} to{" "}
          {Math.min(current * pageSize, total)} out of {total} results
        </span>
        <div className="flex items-center gap-2.5">
          <span className="font-[Poppins] !text-[12px] sm:!text-[13px] font-medium text-black ">
            Rows per page:{" "}
          </span>
          <Select
            className={`footer-btn ${width ? width : ""}`}
            value={pageSize}
            onChange={(size) => handlePageSizeChange(current, size)}
            size="small"
          >
            {pageSizeOptions.map((size) => (
              <Select.Option key={size} value={size} className="!text-center">
                {size}
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>
    </div>
  );
};

export default PaginationTable;
